import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalance as FinanciamentoIcon,
  CardGiftcard as GiftIcon,
  Check as AprovarIcon,
  Close as RejeitarIcon,
  PeopleAlt as PessoasIcon,
  Refresh as RefreshIcon,
  Search as DetectarIcon,
  Settings as SettingsIcon,
  HourglassTop as PendenteIcon,
  Block as InelegivelIcon,
} from '@mui/icons-material';
import service, {
  CampanhaPromo,
  CreditoPromo,
  EstadoCredito,
} from '../../services/marketing-promo-origem.service';
import { PageHeader, StatCard, EmptyState } from '../../components/Marketing';

// Cabo Verde é UTC-1 todo o ano, sem hora de Verão. As datas guardam-se em UTC
// e mostram-se em hora de CV, independentemente do fuso do computador de quem
// abre o backoffice.
const UMA_HORA = 3600 * 1000;
function utcParaCampoCv(iso: string): string {
  return new Date(new Date(iso).getTime() - UMA_HORA).toISOString().slice(0, 16);
}
function campoCvParaUtc(valor: string, segundos = 0): string {
  const base = new Date(`${valor}:${String(segundos).padStart(2, '0')}Z`).getTime();
  return new Date(base + UMA_HORA).toISOString();
}
function dataCv(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(new Date(iso).getTime() - UMA_HORA);
  return d.toISOString().slice(0, 16).replace('T', ' ');
}
const cve = (n: number | null | undefined) => (n == null ? '—' : `${Number(n).toLocaleString('pt-PT')} CVE`);

const SEPARADORES: Array<{ estado: EstadoCredito | 'TODOS'; label: string }> = [
  { estado: 'PENDENTE', label: 'Por aprovar' },
  { estado: 'APROVADO', label: 'Aprovados' },
  { estado: 'REJEITADO', label: 'Rejeitados' },
  { estado: 'INELEGIVEL', label: 'Inelegíveis' },
  { estado: 'TODOS', label: 'Todos' },
];

const COR_ESTADO: Record<EstadoCredito, 'warning' | 'success' | 'default' | 'error'> = {
  PENDENTE: 'warning',
  APROVADO: 'success',
  REJEITADO: 'default',
  INELEGIVEL: 'error',
};

interface FormCampanha {
  activa: boolean;
  exige_codigo: boolean;
  recargas_contam: boolean;
  valor_cve: string;
  limite: string;
  minimo_primeiro_pagamento_cve: string;
  codigos: string;
  inicio: string;
  fim: string;
}

export default function PlanoChoque() {
  const [campanha, setCampanha] = useState<CampanhaPromo | null>(null);
  const [linhas, setLinhas] = useState<CreditoPromo[]>([]);
  const [separador, setSeparador] = useState<EstadoCredito | 'TODOS'>('PENDENTE');
  const [loading, setLoading] = useState(true);
  const [aOcupar, setAOcupar] = useState<number | 'detectar' | 'config' | 'financiamento' | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const [aprovarLinha, setAprovarLinha] = useState<CreditoPromo | null>(null);
  const [rejeitarLinha, setRejeitarLinha] = useState<CreditoPromo | null>(null);
  const [motivo, setMotivo] = useState('');
  const [configAberta, setConfigAberta] = useState(false);
  const [form, setForm] = useState<FormCampanha | null>(null);
  const [finAberto, setFinAberto] = useState(false);
  const [finValor, setFinValor] = useState('');
  const [finRef, setFinRef] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        service.campanha(),
        service.creditos(separador === 'TODOS' ? undefined : separador),
      ]);
      setCampanha(c);
      setLinhas(l);
    } catch (e: any) {
      setToast({ msg: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [separador]);

  useEffect(() => { load(); }, [load]);

  const detectar = async () => {
    setAOcupar('detectar');
    try {
      const r = await service.detectar();
      setToast({
        severity: 'info',
        msg: r.activa
          ? `${r.contasNovas} conta(s) nova(s): ${r.pendentes} por aprovar, ${r.inelegiveis} inelegível(eis), ${r.aEsperar} à espera`
          : 'A campanha está desligada — o detector não fez nada',
      });
      await load();
    } catch (e: any) {
      setToast({ msg: e.message, severity: 'error' });
    } finally {
      setAOcupar(null);
    }
  };

  const confirmarAprovacao = async () => {
    if (!aprovarLinha) return;
    const linha = aprovarLinha;
    setAprovarLinha(null);
    setAOcupar(linha.id);
    try {
      const msg = await service.aprovar(linha.id);
      setToast({ msg, severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e.message, severity: 'error' });
    } finally {
      setAOcupar(null);
    }
  };

  const confirmarRejeicao = async () => {
    if (!rejeitarLinha || !motivo.trim()) return;
    const linha = rejeitarLinha;
    setAOcupar(linha.id);
    try {
      await service.rejeitar(linha.id, motivo.trim());
      setRejeitarLinha(null);
      setMotivo('');
      setToast({ msg: 'Crédito rejeitado', severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e.message, severity: 'error' });
    } finally {
      setAOcupar(null);
    }
  };

  const abrirConfig = () => {
    if (!campanha) return;
    setForm({
      activa: campanha.activa,
      exige_codigo: campanha.exige_codigo,
      recargas_contam: campanha.recargas_contam,
      valor_cve: String(campanha.valor_cve),
      limite: String(campanha.limite),
      minimo_primeiro_pagamento_cve: String(campanha.minimo_primeiro_pagamento_cve),
      codigos: campanha.codigos_validos.join(', '),
      inicio: utcParaCampoCv(campanha.inicio_utc),
      fim: utcParaCampoCv(campanha.fim_utc),
    });
    setConfigAberta(true);
  };

  const guardarConfig = async () => {
    if (!form) return;
    setAOcupar('config');
    try {
      await service.alterarCampanha({
        activa: form.activa,
        exige_codigo: form.exige_codigo,
        recargas_contam: form.recargas_contam,
        valor_cve: Number(form.valor_cve),
        limite: Number(form.limite),
        minimo_primeiro_pagamento_cve: Number(form.minimo_primeiro_pagamento_cve),
        codigos_validos: form.codigos.split(',').map(c => c.trim().toUpperCase()).filter(Boolean),
        inicio_utc: campoCvParaUtc(form.inicio),
        // O fim inclui o último minuto inteiro: 23:59 quer dizer até 23:59:59.
        fim_utc: campoCvParaUtc(form.fim, 59),
      });
      setConfigAberta(false);
      setToast({ msg: 'Campanha actualizada', severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e.message, severity: 'error' });
    } finally {
      setAOcupar(null);
    }
  };

  const guardarFinanciamento = async () => {
    setAOcupar('financiamento');
    try {
      await service.registarFinanciamento(Number(finValor), finRef.trim());
      setFinAberto(false);
      setFinValor('');
      setFinRef('');
      setToast({ msg: 'Financiamento registado no ledger', severity: 'success' });
      await load();
    } catch (e: any) {
      setToast({ msg: e.message, severity: 'error' });
    } finally {
      setAOcupar(null);
    }
  };

  const faltaCobertura = campanha && campanha.financiado_cve !== null
    && campanha.financiado_cve < campanha.comprometido_cve;

  return (
    <Box>
      <PageHeader
        title="Plano de Choque"
        subtitle="Crédito de 200 CVE por código de origem — aprovação manual"
        icon={<GiftIcon />}
        actions={
          <>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Atualizar</Button>
            <Button startIcon={aOcupar === 'detectar' ? <CircularProgress size={16} /> : <DetectarIcon />}
              onClick={detectar} disabled={aOcupar !== null}>
              Detectar agora
            </Button>
            <Button startIcon={<FinanciamentoIcon />} onClick={() => setFinAberto(true)}>Financiamento</Button>
            <Button variant="contained" startIcon={<SettingsIcon />} onClick={abrirConfig} disabled={!campanha}>
              Configurar
            </Button>
          </>
        }
      />

      {campanha && !campanha.activa && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          A campanha está <b>desligada</b>: o detector não procura candidatos e nenhum crédito pode ser aprovado.
          Liga-a em <b>Configurar</b> no dia do lançamento.
        </Alert>
      )}
      {faltaCobertura && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Há <b>{cve(campanha!.comprometido_cve)}</b> creditados e só <b>{cve(campanha!.financiado_cve)}</b> transferidos
          para a conta segregada. A verificação diária de cobertura vai assinalar a diferença até as finanças
          transferirem o valor e o registares em <b>Financiamento</b>.
        </Alert>
      )}
      {campanha && !campanha.exige_codigo && campanha.activa && (
        <Alert severity="info" sx={{ mb: 2 }}>
          O código de origem <b>não</b> está a ser exigido: qualquer conta nova que cumpra as outras regras entra na lista.
        </Alert>
      )}

      {campanha && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatCard label="Atribuídos" value={`${campanha.atribuidos} / ${campanha.limite}`}
              helper={`${cve(campanha.valor_cve)} cada`} icon={<GiftIcon />} color="success" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Por aprovar" value={campanha.contagens.PENDENTE} icon={<PendenteIcon />} color="warning" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Comprometido / financiado"
              value={`${cve(campanha.comprometido_cve)}`}
              helper={`financiado: ${cve(campanha.financiado_cve)}`}
              icon={<FinanciamentoIcon />} color={faltaCobertura ? 'error' : 'info'} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Inelegíveis" value={campanha.contagens.INELEGIVEL}
              helper={`${campanha.contagens.REJEITADO} rejeitado(s)`} icon={<InelegivelIcon />} color="error" />
          </Grid>
        </Grid>
      )}

      {campanha && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Contas criadas de <b>{dataCv(campanha.inicio_utc)}</b> a <b>{dataCv(campanha.fim_utc)}</b> (hora de Cabo Verde) ·
          primeiro pagamento de serviço ≥ <b>{cve(campanha.minimo_primeiro_pagamento_cve)}</b> ·
          códigos: <b>{campanha.codigos_validos.join(', ')}</b>
          {campanha.recargas_contam ? ' · recargas contam' : ' · recargas não contam'}
        </Typography>
      )}

      <Paper>
        <Tabs value={separador} onChange={(_, v) => setSeparador(v)} variant="scrollable" allowScrollButtonsMobile>
          {SEPARADORES.map(s => (
            <Tab key={s.estado} value={s.estado}
              label={s.estado !== 'TODOS' && campanha ? `${s.label} (${campanha.contagens[s.estado]})` : s.label} />
          ))}
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
        ) : linhas.length === 0 ? (
          <EmptyState compact icon={<PessoasIcon />} title="Sem candidatos"
            description={separador === 'PENDENTE'
              ? 'Ninguém à espera de aprovação. O detector corre de 15 em 15 minutos.'
              : 'Nada neste estado.'} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pessoa</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>CNI</TableCell>
                  <TableCell align="right">1.º pagamento de serviço</TableCell>
                  <TableCell>Registo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acções</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {linhas.map(l => (
                  <TableRow key={l.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{l.nome || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{l.email || l.user_id}</Typography>
                      {l.telefone && <Typography variant="caption" color="text.secondary">{l.telefone}</Typography>}
                    </TableCell>
                    <TableCell>{l.codigo_origem ? <Chip size="small" label={l.codigo_origem} /> : '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{l.cni_normalizado || '—'}</Typography>
                      <Typography variant="caption" color={l.cni_verificada ? 'success.main' : 'text.secondary'}>
                        {l.cni_verificada ? 'CNI marcada como verificada' : 'KYC bancário'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{cve(l.pagamento_valor_cve)}</Typography>
                      <Typography variant="caption" color="text.secondary">{dataCv(l.pagamento_em)}</Typography>
                    </TableCell>
                    <TableCell>{dataCv(l.registado_em)}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Chip size="small" color={COR_ESTADO[l.estado]} label={l.estado} />
                      {l.motivo && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {l.motivo}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {l.estado === 'PENDENTE' && (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title={campanha?.activa ? 'Aprovar e creditar' : 'A campanha está desligada'}>
                            <span>
                              <Button size="small" variant="contained" color="success"
                                startIcon={aOcupar === l.id ? <CircularProgress size={14} color="inherit" /> : <AprovarIcon />}
                                disabled={aOcupar !== null || !campanha?.activa}
                                onClick={() => setAprovarLinha(l)}>
                                Aprovar
                              </Button>
                            </span>
                          </Tooltip>
                          <Button size="small" color="inherit" startIcon={<RejeitarIcon />}
                            disabled={aOcupar !== null}
                            onClick={() => { setRejeitarLinha(l); setMotivo(''); }}>
                            Rejeitar
                          </Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Aprovar */}
      <Dialog open={!!aprovarLinha} onClose={() => setAprovarLinha(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Aprovar crédito</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Creditar <b>{cve(campanha?.valor_cve)}</b> na carteira de <b>{aprovarLinha?.nome || aprovarLinha?.email}</b>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Código {aprovarLinha?.codigo_origem || '—'} · CNI {aprovarLinha?.cni_normalizado || 'sem número'} ·
            1.º pagamento {cve(aprovarLinha?.pagamento_valor_cve)}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            O KYC da app não confirma a identidade. Confirma que é uma pessoa real e que não tem outra conta antes de aprovar —
            o dinheiro cai na carteira de imediato e não há reversão automática.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAprovarLinha(null)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={confirmarAprovacao}>Aprovar e creditar</Button>
        </DialogActions>
      </Dialog>

      {/* Rejeitar */}
      <Dialog open={!!rejeitarLinha} onClose={() => setRejeitarLinha(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rejeitar crédito</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {rejeitarLinha?.nome || rejeitarLinha?.email} fica sem o crédito desta campanha.
          </Typography>
          <TextField label="Motivo" value={motivo} onChange={e => setMotivo(e.target.value)}
            fullWidth required multiline minRows={2} inputProps={{ maxLength: 255 }}
            placeholder="Ex.: mesma pessoa que outra conta" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejeitarLinha(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmarRejeicao}
            disabled={!motivo.trim() || aOcupar !== null}>
            Rejeitar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configurar */}
      <Dialog open={configAberta} onClose={() => setConfigAberta(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configurar campanha</DialogTitle>
        <DialogContent>
          {form && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControlLabel
                control={<Switch checked={form.activa} onChange={e => setForm({ ...form, activa: e.target.checked })} />}
                label="Campanha activa" />
              <FormControlLabel
                control={<Switch checked={form.exige_codigo} onChange={e => setForm({ ...form, exige_codigo: e.target.checked })} />}
                label="Exigir código de origem" />
              <FormControlLabel
                control={<Switch checked={form.recargas_contam} onChange={e => setForm({ ...form, recargas_contam: e.target.checked })} />}
                label="Recargas de telemóvel contam como serviço" />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Valor (CVE)" type="number" value={form.valor_cve}
                  onChange={e => setForm({ ...form, valor_cve: e.target.value })} fullWidth />
                <TextField label="Limite de créditos" type="number" value={form.limite}
                  onChange={e => setForm({ ...form, limite: e.target.value })} fullWidth
                  helperText={campanha ? `Já atribuídos: ${campanha.atribuidos}` : ''} />
              </Stack>
              <TextField label="Mínimo do 1.º pagamento de serviço (CVE)" type="number"
                value={form.minimo_primeiro_pagamento_cve}
                onChange={e => setForm({ ...form, minimo_primeiro_pagamento_cve: e.target.value })} />
              <TextField label="Códigos válidos" value={form.codigos}
                onChange={e => setForm({ ...form, codigos: e.target.value })}
                helperText="Separados por vírgula" />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Contas criadas a partir de (hora CV)" type="datetime-local" value={form.inicio}
                  onChange={e => setForm({ ...form, inicio: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                <TextField label="Fim da campanha (hora CV)" type="datetime-local" value={form.fim}
                  onChange={e => setForm({ ...form, fim: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigAberta(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarConfig} disabled={aOcupar === 'config'}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Financiamento */}
      <Dialog open={finAberto} onClose={() => setFinAberto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Registar financiamento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A transferência que as finanças fizeram da conta de operação para a conta segregada, para cobrir os créditos.
            Regista o valor efectivamente movido e a referência do banco — a mesma referência não se regista duas vezes.
          </Typography>
          <Stack spacing={2}>
            <TextField label="Valor transferido (CVE)" type="number" value={finValor}
              onChange={e => setFinValor(e.target.value)} fullWidth />
            <TextField label="Referência bancária" value={finRef}
              onChange={e => setFinRef(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinAberto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarFinanciamento}
            disabled={!(Number(finValor) > 0) || !finRef.trim() || aOcupar === 'financiamento'}>
            Registar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}
